import { memo, useMemo, type MouseEvent } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronRightRounded, MoreHorizRounded } from "@mui/icons-material";
import { focusRingSx, touchTargetSx } from "../../overviewPanel";
import { CompanyIdentityRow } from "../../../polish/CompanyIdentityRow";
import { toneTextColour } from "../../../polish/LabelValueRow";
import { TABULAR, type TonalTokens } from "../../../polish/tokens";
import {
  itemKey,
  metricComparisons,
  periodBlock,
  recentRow,
  reportLine,
  summaryParts,
  summaryText,
  type RecentRow,
  type SummaryPart,
} from "./feedModel";
import { MetricBars } from "./MetricBars";
import { Rating, Target, cellSx } from "./NoteParts";
import { SinceReport, sinceReportShown } from "./SinceReport";
import {
  CARD_OPEN_CLASS,
  REPORT_PANEL_CLASS,
  cardButtonSx,
  controlSx,
  passThroughSx,
  reportRowSx,
} from "./openableCard";
import type { EarningsFeedItem } from "./types";

/** contain-intrinsic-size: a wide row's height while content-visibility skips it. */
export const RESULTS_CARD_HEIGHT = 300;

export const NO_NOTES_LINE = "No analyst notes";

/** The results card beside its notes card from md; the notes card under it below. */
export const REPORT_ROW_COLUMNS = {
  xs: "minmax(0, 1fr)",
  md: "minmax(0, 1.4fr) minmax(0, 1fr)",
} as const;

/** The gap between a report's two cards: apart, but read as one unit. */
export const REPORT_ROW_GAP = 1.5;

/** The gap between two reports on the same day: twice the gap within one, so each pair reads as its own unit. */
export const REPORT_ROWS_GAP = 3;

/** A card's padding, a step roomier from md. */
export const PANEL_PADDING = {
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 2.5 },
} as const;

export const NOTE_ROW_HEIGHT = 38;
/**
 * One grid for every row, so ratings start at one place and the target
 * column is as wide as the widest target in the card.
 */
const NOTE_COLUMNS = "minmax(0, 1.15fr) minmax(0, 1fr) max-content";

const NOTE_TEXT = { xs: "0.8125rem", md: "0.875rem" } as const;

const noteCellSx = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  minHeight: NOTE_ROW_HEIGHT,
} as const;

/** The quarter's block, set as Since report's beside it. */
const statLabelSx = {
  fontSize: { xs: "0.75rem", md: "0.8125rem" },
  lineHeight: "16px",
  fontWeight: 500,
  color: "text.secondary",
} as const;

const statValueSx = {
  fontSize: { xs: "1.125rem", md: "1.3125rem" },
  lineHeight: { xs: "24px", md: "28px" },
  fontWeight: 700,
  color: "text.primary",
} as const;

interface StatDividerProps {
  tokens: TonalTokens;
  /** Between the company and the figures: none on a phone, where they wrap under it. */
  leading?: boolean;
}

const StatDivider = ({ tokens, leading = false }: StatDividerProps) => (
  <Box
    aria-hidden
    sx={{
      display: leading ? { xs: "none", sm: "block" } : "block",
      flex: "none",
      width: "1px",
      height: 36,
      backgroundColor: tokens.hairline,
    }}
  />
);

interface SeparatorProps {
  tokens: TonalTokens;
}

/** " · ", its spaces kept at either end of a part. */
const Separator = ({ tokens }: SeparatorProps) => (
  <Box
    component="span"
    aria-hidden
    sx={{ color: tokens.faint, whiteSpace: "pre" }}
  >
    {" · "}
  </Box>
);

interface SummaryLineProps {
  parts: SummaryPart[];
  tokens: TonalTokens;
}

/**
 * "↑ 12 raised · ↓ 3 cut · ⇩ 1 downgrade · 14 firms": the glyph toned, the
 * count, the word. A long line wraps between counts, each keeping its
 * separator at the end of its line.
 */
const SummaryLine = ({ parts, tokens }: SummaryLineProps) => (
  <Typography
    component="div"
    data-testid="notes-summary"
    sx={{
      ...TABULAR,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      minWidth: 0,
      fontSize: { xs: "0.875rem", md: "0.9375rem" },
      lineHeight: "24px",
    }}
  >
    {parts.map((part, index) => (
      <Box
        key={part.key}
        component="span"
        data-testid={`notes-summary-${part.key}`}
        sx={{ flex: "none", whiteSpace: "nowrap" }}
      >
        {part.glyph !== null && (
          <>
            <Box
              component="span"
              data-testid="summary-glyph"
              sx={{ color: toneTextColour(part.tone, tokens), fontWeight: 600 }}
            >
              {part.glyph}
            </Box>{" "}
          </>
        )}
        <Box component="span" sx={{ color: tokens.text, fontWeight: 600 }}>
          {part.count}
        </Box>{" "}
        <Box component="span" sx={{ color: tokens.muted }}>
          {part.label}
        </Box>
        {index < parts.length - 1 && <Separator tokens={tokens} />}
      </Box>
    ))}
  </Typography>
);

interface NoteLineProps {
  row: RecentRow;
  tokens: TonalTokens;
}

/** One note's cells in the card's grid: firm, rating, target from → to, toned as the report page's notes table; no date. */
const NoteLine = ({ row, tokens }: NoteLineProps) => (
  <Box data-testid="card-note" sx={{ display: "contents" }}>
    <Box
      aria-hidden
      sx={{ gridColumn: "1 / -1", height: "1px", bgcolor: tokens.hairline }}
    />
    <Box sx={noteCellSx}>
      <Typography
        component="span"
        data-testid="note-firm"
        sx={{ ...cellSx, fontWeight: 500, color: tokens.text }}
      >
        {row.firm}
      </Typography>
    </Box>
    <Box sx={noteCellSx}>
      <Rating row={row} tokens={tokens} />
    </Box>
    <Box sx={{ ...noteCellSx, justifyContent: "flex-end" }}>
      <Target row={row} tokens={tokens} />
    </Box>
  </Box>
);

interface ResultsCardProps {
  item: EarningsFeedItem;
  tokens: TonalTokens;
  isDark: boolean;
  reduced: boolean;
  onOpenReport: (eventId: string, card: HTMLElement) => void;
  onOpenCompany: (symbol: string) => void;
  onOpenMenu: (anchor: HTMLElement, symbol: string) => void;
}

/**
 * One report as a row of two cards read as one unit. The results card: who,
 * the move since the report beside them, and EPS and revenue against their
 * estimates when the results are in. The notes card: the analyst notes'
 * counts, the newest four and, when there are more, View all. Either card
 * opens the report's page; the identity, Since report, ⋯ and View all are
 * their own controls. Label and number only.
 */
const Card = ({
  item,
  tokens,
  isDark,
  reduced,
  onOpenReport,
  onOpenCompany,
  onOpenMenu,
}: ResultsCardProps) => {
  const { company, report } = item;
  const metrics = metricComparisons({
    eps: item.eps,
    revenue: item.revenue,
    reportingCurrency: company.reportingCurrency,
  });
  const since = sinceReportShown(item.sinceReport, company.priceCurrency);
  const block = periodBlock(item.period);
  const line = reportLine(report.reportDate, report.callTime);
  const summary = item.notes.total > 0 ? `, ${summaryText(item.notes)}` : "";
  const parts = useMemo(() => summaryParts(item.notes), [item.notes]);
  const rows = useMemo(() => item.recent.map(recentRow), [item.recent]);
  const more = item.notes.total > rows.length;
  const open = (click: MouseEvent<HTMLElement>) =>
    onOpenReport(
      report.eventId,
      click.currentTarget.closest("article") ?? click.currentTarget,
    );

  return (
    <Box
      component="article"
      data-testid="results-card"
      data-feed-key={itemKey(item)}
      sx={{
        ...reportRowSx(tokens, RESULTS_CARD_HEIGHT),
        display: "grid",
        gridTemplateColumns: REPORT_ROW_COLUMNS,
        gap: REPORT_ROW_GAP,
      }}
    >
      <Box
        className={REPORT_PANEL_CLASS}
        data-testid="results-panel"
        sx={PANEL_PADDING}
      >
        <Box
          component="button"
          type="button"
          className={CARD_OPEN_CLASS}
          aria-label={`${company.symbol} report, ${line}${summary}. Open the report`}
          data-testid="results-card-open"
          onClick={open}
          sx={cardButtonSx(tokens)}
        />
        <Box sx={passThroughSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              data-testid="results-header"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                columnGap: { xs: 1.5, md: 2.5 },
                rowGap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                component="button"
                type="button"
                aria-label={`Open ${company.companyName} earnings`}
                data-testid="results-identity"
                onClick={() => onOpenCompany(company.symbol)}
                sx={{
                  all: "unset",
                  ...controlSx,
                  ...focusRingSx,
                  display: "block",
                  // A phone gives the company its own line, the figures under it.
                  flex: { xs: "1 0 100%", sm: "0 1 auto" },
                  minWidth: 0,
                  borderRadius: "8px",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  "@media (hover: hover)": {
                    "&:hover .identity-ticker": { color: tokens.gain },
                  },
                }}
              >
                <CompanyIdentityRow
                  symbol={company.symbol}
                  name={company.companyName}
                  exchange={company.exchange}
                  large
                />
              </Box>
              {(block !== null || since) && (
                <Box
                  data-testid="results-stats"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2.5 },
                    flex: "none",
                  }}
                >
                  <StatDivider tokens={tokens} leading />
                  {block !== null && (
                    <Box data-testid="results-period">
                      <Typography sx={statLabelSx}>{block.label}</Typography>
                      <Typography sx={statValueSx}>{block.value}</Typography>
                    </Box>
                  )}
                  {block !== null && since && <StatDivider tokens={tokens} />}
                  {since && (
                    <Box sx={{ ...controlSx, ml: -0.5 }}>
                      <SinceReport
                        since={item.sinceReport}
                        priceCurrency={company.priceCurrency}
                        isDark={isDark}
                        align="left"
                        size="md"
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              aria-label={`More for ${company.symbol}`}
              aria-haspopup="menu"
              onClick={(click: MouseEvent<HTMLElement>) =>
                onOpenMenu(click.currentTarget, company.symbol)
              }
              sx={{
                ...controlSx,
                alignSelf: "flex-start",
                color: tokens.faint,
                mr: -0.75,
                mt: -0.5,
                ...touchTargetSx,
                ...focusRingSx,
              }}
            >
              <MoreHorizRounded fontSize="small" />
            </IconButton>
          </Box>

          {metrics.length > 0 && (
            <Box
              data-testid="metrics"
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                columnGap: { xs: 2, md: 4 },
                mt: { xs: 2, md: 2.5 },
              }}
            >
              {metrics.map((metric) => (
                <MetricBars
                  key={metric.key}
                  metric={metric}
                  tokens={tokens}
                  isDark={isDark}
                  reduced={reduced}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        className={REPORT_PANEL_CLASS}
        data-testid="card-notes"
        sx={{ ...PANEL_PADDING, display: "flex", flexDirection: "column" }}
      >
        <Box
          component="button"
          type="button"
          className={CARD_OPEN_CLASS}
          aria-label={`${company.symbol} analyst notes${summary}. Open the report`}
          data-testid="notes-card-open"
          onClick={open}
          sx={cardButtonSx(tokens)}
        />
        <Box
          sx={{
            ...passThroughSx,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          {parts.length > 0 && <SummaryLine parts={parts} tokens={tokens} />}
          {parts.length === 0 && (
            <Typography
              data-testid="card-notes-empty"
              sx={{
                fontSize: { xs: "0.875rem", md: "0.9375rem" },
                lineHeight: "24px",
                color: tokens.faint,
              }}
            >
              {NO_NOTES_LINE}
            </Typography>
          )}
          {rows.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: NOTE_COLUMNS,
                columnGap: 2,
                mt: 1.25,
              }}
            >
              {rows.map((row) => (
                <NoteLine key={row.id} row={row} tokens={tokens} />
              ))}
            </Box>
          )}
          {more && (
            <Box
              component="button"
              type="button"
              data-testid="view-all-notes"
              onClick={open}
              sx={{
                all: "unset",
                ...controlSx,
                ...focusRingSx,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                alignSelf: "flex-start",
                mt: "auto",
                pt: 1.25,
                borderRadius: "6px",
                fontSize: NOTE_TEXT,
                lineHeight: "20px",
                fontWeight: 500,
                color: tokens.muted,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                transition: "color 120ms ease",
                "@media (hover: hover)": { "&:hover": { color: tokens.text } },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                },
              }}
            >
              {`View all ${item.notes.total} notes`}
              <ChevronRightRounded
                aria-hidden
                sx={{ fontSize: 18, mr: -0.5 }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const ResultsCard = memo(Card);
